// components/CreateCustomPlanModal.tsx
import React, { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { CheckCircle, Loader2, PackagePlus, X } from '@/components/ui/lucide-memo';
import Switch from '@/components/ui/Switch';

interface CreateCustomPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (planDetails: PlanDetails) => Promise<void>;
  targetOrgId: string;
  targetOrgName: string;
}

export interface PlanDetails {
  organisationId: string;
  planName: string;
  description: string | null;
  includedCredits: number;
  priceAmount: number;
  priceCurrency: string;
  accessScope: 'CHEAP_MODELS_ONLY' | null;
  grantImmediately: boolean;
}

const inputClass =
  'h-10 w-full rounded-[12px] border border-[#2F2F2F] bg-transparent px-3 !text-[15px] text-[#ECECEC] outline-none placeholder:text-[#8E8E8E] focus:border-[#8E8E8E] disabled:cursor-not-allowed disabled:opacity-50';

const secondaryButtonClass =
  'inline-flex h-9 items-center justify-center rounded-full px-4 !text-[15px] text-[#ECECEC] transition-colors hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40';

const primaryButtonClass =
  'inline-flex h-9 min-w-24 shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-[#F0F0F0] px-4 !text-[15px] font-medium text-black transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-40';

const dollarsToCents = (dollarString: string): number | null => {
  if (typeof dollarString !== 'string' || dollarString.trim() === '') return null;
  const cleanedString = dollarString.replace(/[^0-9.]/g, '');
  const value = Number.parseFloat(cleanedString);
  if (Number.isNaN(value) || value < 0) return null;
  return Math.round(value * 100);
};

const CreateCustomPlanModal: React.FC<CreateCustomPlanModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  targetOrgId,
  targetOrgName,
}) => {
  const [planName, setPlanName] = useState('');
  const [description, setDescription] = useState('');
  const [includedCreditsInput, setIncludedCreditsInput] = useState('0');
  const [priceInput, setPriceInput] = useState('');
  const [priceCurrency, setPriceCurrency] = useState('usd');
  const [accessScope, setAccessScope] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [isImmediateGrant, setIsImmediateGrant] = useState(false);
  const [isManualGrant, setIsManualGrant] = useState(false);
  const [grantImmediately, setGrantImmediately] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
    setPlanName('');
    setDescription('');
    setIncludedCreditsInput('0');
    setPriceInput('');
    setPriceCurrency('usd');
    setAccessScope('');
    setError(null);
    setIsImmediateGrant(false);
    setIsManualGrant(false);
    setGrantImmediately(false);
    setIsSubmitting(false);
    setStep('form');
  }, [isOpen, targetOrgId]);

  const handlePlanNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setPlanName(event.target.value);
  }, []);

  const handleDescriptionChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(event.target.value);
  }, []);

  const handleIncludedCreditsChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setIncludedCreditsInput(event.target.value);
  }, []);

  const handleAccessScopeChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    setAccessScope(event.target.value);
  }, []);

  const handlePriceChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setPriceInput(event.target.value);
  }, []);

  const handleCurrencyChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setPriceCurrency(event.target.value);
  }, []);

  const handleSubmit = useCallback(async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!planName.trim()) {
      setError('Plan name is required.');
      return;
    }
    if (planName.trim().length > 80) {
      setError('Plan name cannot exceed 80 characters.');
      return;
    }
    if (description.trim().length > 5000) {
      setError('Description cannot exceed 5,000 characters.');
      return;
    }

    const credits = Number.parseInt(includedCreditsInput, 10);
    if (Number.isNaN(credits) || credits < 0 || includedCreditsInput === '') {
      setError('Included credits must be a non-negative whole number.');
      return;
    }

    const currency = priceCurrency.trim().toLowerCase();
    if (currency.length !== 3 || !/^[a-z]{3}$/.test(currency)) {
      setError('Currency must use a three-letter code, for example usd.');
      return;
    }

    const centsAmount = dollarsToCents(priceInput);
    if (centsAmount === null) {
      setError('Price must be a valid non-negative number, for example 100.00.');
      return;
    }

    setIsSubmitting(true);
    try {
      const planDetails: PlanDetails = {
        organisationId: targetOrgId,
        planName: planName.trim(),
        description: description.trim() || null,
        includedCredits: credits,
        priceAmount: centsAmount,
        priceCurrency: currency,
        accessScope: accessScope === '' ? null : accessScope as 'CHEAP_MODELS_ONLY',
        grantImmediately,
      };
      await onSubmit(planDetails);
      const immediateGrant = grantImmediately || centsAmount === 0;
      setIsImmediateGrant(immediateGrant);
      setIsManualGrant(grantImmediately && centsAmount > 0);
      setStep('success');
    } catch (submitError: unknown) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to create custom plan.');
      setStep('form');
    } finally {
      setIsSubmitting(false);
    }
  }, [planName, description, includedCreditsInput, priceCurrency, priceInput, targetOrgId, accessScope, grantImmediately, onSubmit]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="relative max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-[24px] border border-white/[0.06] bg-[#212121] text-white shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-white"
          aria-label="Close modal"
          disabled={isSubmitting}
        >
          <X size={17} />
        </button>

        {step === 'form' ? (
          <>
            <header className="border-b border-white/[0.055] px-5 py-4 pr-12">
              <div className="flex items-center gap-2">
                <PackagePlus size={16} className="text-white/35" />
                <h2 className="text-[18px] font-medium text-[#ECECEC]">Create a custom plan</h2>
              </div>
              <p className="mt-1 text-sm text-white/45">Configure a plan for {targetOrgName}.</p>
            </header>

            <form onSubmit={handleSubmit}>
              <div className="space-y-5 p-5">
                <section className="space-y-3">
                  <div>
                    <h3 className="text-sm font-medium text-white/80">Plan details</h3>
                    <p className="mt-0.5 text-xs text-white/35">Set the name, credits, and model access.</p>
                  </div>

                  <div>
                    <label htmlFor="plan-name" className="mb-1 block text-xs font-medium text-white/55">
                      Plan name <span className="text-[#FF7369]" aria-hidden="true">*</span>
                    </label>
                    <input
                      type="text"
                      id="plan-name"
                      value={planName}
                      onChange={handlePlanNameChange}
                      required
                      maxLength={80}
                      className={inputClass}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label htmlFor="plan-desc" className="mb-1 block text-xs font-medium text-white/55">
                      Description <span className="font-normal text-white/30">(optional)</span>
                    </label>
                    <textarea
                      id="plan-desc"
                      value={description}
                      onChange={handleDescriptionChange}
                      rows={2}
                      maxLength={5000}
                      className={`${inputClass} min-h-20 resize-y py-2`}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label htmlFor="plan-credits" className="mb-1 block text-xs font-medium text-white/55">
                        Included credits <span className="text-[#FF7369]" aria-hidden="true">*</span>
                      </label>
                      <input
                        type="number"
                        id="plan-credits"
                        min="0"
                        step="1"
                        value={includedCreditsInput}
                        onChange={handleIncludedCreditsChange}
                        required
                        className={inputClass}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <label htmlFor="plan-scope" className="mb-1 block text-xs font-medium text-white/55">
                        Model access
                      </label>
                      <select
                        id="plan-scope"
                        value={accessScope}
                        onChange={handleAccessScopeChange}
                        className={inputClass}
                        disabled={isSubmitting}
                      >
                        <option value="">All models</option>
                        <option value="CHEAP_MODELS_ONLY">Cost-efficient models only</option>
                      </select>
                    </div>
                  </div>
                </section>

                <section className="space-y-3 border-t border-white/[0.055] pt-5">
                  <div>
                    <h3 className="text-sm font-medium text-white/80">Price</h3>
                    <p className="mt-0.5 text-xs text-white/35">Used for checkout or manual invoice records.</p>
                  </div>

                  <div className="grid grid-cols-[minmax(0,1fr)_96px] gap-3">
                    <div>
                      <label htmlFor="plan-price-input" className="mb-1 block text-xs font-medium text-white/55">
                        Amount <span className="text-[#FF7369]" aria-hidden="true">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          id="plan-price-input"
                          value={priceInput}
                          placeholder="100.00"
                          onChange={handlePriceChange}
                          required
                          inputMode="decimal"
                          className={`${inputClass} pl-7`}
                          disabled={isSubmitting}
                        />
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-white/35">
                          {priceCurrency.toLowerCase() === 'usd' ? '$' : priceCurrency.toLowerCase() === 'eur' ? '€' : priceCurrency.toLowerCase() === 'gbp' ? '£' : ''}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="plan-currency" className="mb-1 block text-xs font-medium text-white/55">
                        Currency <span className="text-[#FF7369]" aria-hidden="true">*</span>
                      </label>
                      <input
                        type="text"
                        id="plan-currency"
                        value={priceCurrency}
                        onChange={handleCurrencyChange}
                        required
                        maxLength={3}
                        placeholder="usd"
                        className={inputClass}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4 border-t border-white/[0.055] py-3">
                    <div>
                      <span className="text-sm font-medium text-white/75">Grant immediately</span>
                      <p className="mt-0.5 text-xs text-white/35">
                        Apply credits now as a manual payment and skip checkout.
                      </p>
                    </div>
                    <Switch
                      checked={grantImmediately}
                      onCheckedChange={setGrantImmediately}
                      disabled={isSubmitting}
                      labelRight=""
                    />
                  </div>
                </section>

                {error && <p className="text-sm text-[#FF7369]">{error}</p>}
              </div>

              <footer className="flex justify-end gap-2 border-t border-white/[0.055] px-5 py-3">
                <button type="button" onClick={onClose} disabled={isSubmitting} className={secondaryButtonClass}>
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting} className={primaryButtonClass}>
                  {isSubmitting ? <Loader2 size={15} className="animate-spin" /> : 'Create plan'}
                </button>
              </footer>
            </form>
          </>
        ) : (
          <div className="p-6 text-center">
            <CheckCircle size={34} className="mx-auto text-[#4DAB9A]" />
            <h2 className="mt-3 text-[18px] font-medium text-[#ECECEC]">
              {isImmediateGrant ? 'Plan granted' : 'Custom plan created'}
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-white/50">
              {isImmediateGrant
                ? isManualGrant
                  ? <>The plan &quot;<span className="font-medium text-white/75">{planName}</span>&quot; was granted to <span className="font-medium text-white/75">{targetOrgName}</span> and marked as manually paid.</>
                  : <>The plan &quot;<span className="font-medium text-white/75">{planName}</span>&quot; was granted to <span className="font-medium text-white/75">{targetOrgName}</span>.</>
                : <>The plan &quot;<span className="font-medium text-white/75">{planName}</span>&quot; is now available to <span className="font-medium text-white/75">{targetOrgName}</span>.</>}
            </p>
            <button type="button" onClick={onClose} className={`${primaryButtonClass} mt-5`}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateCustomPlanModal;
